export type ValidRedReviewAction =
    | 'keep'
    | 'revise'
    | 'replace_evidence'
    | 'add_evidence'
    | 'reject';

export type RedReviewAction = ValidRedReviewAction | '';

const VALID_RED_REVIEW_ACTIONS = new Set<ValidRedReviewAction>([
    'keep',
    'revise',
    'replace_evidence',
    'add_evidence',
    'reject'
]);

/**
 * Unknown or transitional values deliberately fall back to the blue result so
 * that an unfinished review never leaks a partially generated red result into
 * the page.
 */
export const normalizeRedReviewAction = (reviewAction?: unknown): RedReviewAction => {
    const normalizedAction = String(reviewAction || '').trim().toLowerCase();
    return VALID_RED_REVIEW_ACTIONS.has(normalizedAction as ValidRedReviewAction)
        ? normalizedAction as ValidRedReviewAction
        : '';
};

/**
 * Some SSE/history payloads put review_action on the group while others put it
 * on a mapping row. Prefer the group value and inspect rows only when it is
 * absent, keeping both response shapes on the same selection rule.
 */
export const resolveRedReviewAction = (redGroup: any): RedReviewAction => {
    const groupAction = normalizeRedReviewAction(redGroup?.review_action);
    if (groupAction) {
        return groupAction;
    }

    const redMappings = Array.isArray(redGroup?.mappings) ? redGroup.mappings : [];
    for (const redMapping of redMappings) {
        const mappingAction = normalizeRedReviewAction(redMapping?.review_action);
        if (mappingAction) {
            return mappingAction;
        }
    }

    return '';
};

/**
 * Red feature_index is one-based and points to the corresponding blue result.
 * A red-only group is not renderable because it cannot be proven to belong to
 * the current claim feature.
 */
export const findMatchingBlueMappingGroup = (redGroup: any, blueAllMappings: any[] = []) => {
    const blueIndex = Number(redGroup?.feature_index) - 1;
    return Number.isInteger(blueIndex) ? (blueAllMappings[blueIndex] || null) : null;
};

/**
 * Review metadata is attached separately so it can drive the support/oppose
 * card without changing which side supplies the analysis content.
 */
export const attachRedReviewMetadata = (
    sourceGroup: any,
    redGroup: any,
    reviewAction: RedReviewAction
) => {
    const sourceMappings = Array.isArray(sourceGroup?.mappings) ? sourceGroup.mappings : [];
    const redMappings = Array.isArray(redGroup?.mappings) ? redGroup.mappings : [];
    const groupReviewAnalysis = redGroup?.review_analysis || '';
    const groupReviewChallenge = redGroup?.review_challenge ||
        groupReviewAnalysis ||
        redGroup?.reason ||
        '';
    const groupReviewStrength = redGroup?.review_evidence_strength ??
        redGroup?.evidence_strength;

    return {
        ...sourceGroup,
        review_action: reviewAction || undefined,
        review_analysis: groupReviewAnalysis,
        review_challenge: groupReviewChallenge,
        review_evidence_strength: groupReviewStrength,
        mappings: sourceMappings.map((sourceMapping: any, index: number) => {
            const redMapping = redMappings[index] || {};
            const reviewAnalysis = redMapping.review_analysis ?? groupReviewAnalysis;

            return {
                ...sourceMapping,
                review_action: reviewAction || undefined,
                review_analysis: reviewAnalysis,
                review_challenge: redMapping.review_challenge ??
                    redMapping.challenge ??
                    reviewAnalysis ??
                    groupReviewChallenge,
                review_evidence_strength: redMapping.review_evidence_strength ??
                    redMapping.evidence_strength ??
                    groupReviewStrength,
            };
        }),
    };
};
